// ucusPlani.js — GÖREVİN UÇUŞ PLANI: AYNI GÜN · AYNI ROTA · AYNI ŞİRKET — TEK KAYNAK (21 Eki 2026)
//
// 🔑 Serkan (21 Eki 2026, A): *"Yalnız aynı günün planı kullanılır. Yoksa otomatik doldurma olmaz, planlamacı kendisi
//    seçer. Sistem tahmin etmez."* · *"Bu veri planlamacı planlarken giriyor, ilgili plan ile uyumlu olmazsa mismatch
//    veriyor."* — uyuşmazlığı arşiv (uçuşun KENDİ planıyla, `archive-flight`) ve EDIT DUTY ("DOES NOT MATCH FLIGHT
//    PLAN") söyler; ikisinin de doğru plana bakması gerekir.
// 🔴 ÖNCESİ (kod taraması W4b, ölçüldü): aynı arama FTLPanel'de ÜÇ kopyaydı (EDIT DUTY · ASSIGN bacak tipi · ASSIGN
//    plan ipucu). Aynı gün plan yoksa rotanın BAŞKA günkü planı alınıyordu (`|| data[0]`) ve kaynağı yine "flight plan"
//    yazıyordu: operasyon tipi (dolayısıyla azami UGS tablosu) ve asgari ekip kapısının uçağı başka günün planından
//    geliyor, EDIT DUTY başka günün planıyla "DOES NOT MATCH" diyebiliyordu. Ayrıca okuma hatası okunmuyordu (düşünce
//    "NO MATCHING FLIGHT PLAN FOUND"), şirket süzgeci yoktu, silinmiş plan da eşleşebiliyordu ve en çok 20 plan
//    sırasız okunduğu için yoğun rotada o günün planı listeye hiç girmeyebiliyordu.
import { supabase } from '../supabaseClient';
import { SAYILI, hepsiniOku, okumaHataMetni } from './kesikOkuma';

const AYLAR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** Plan tarihi → Date (UTC gün başı). İki biçim: ISO ("2026-08-03") ve OFP biçimi ("03 AUG 2026"). Başka biçim
 *  tahmin edilmez → null.
 *  🔴 4 Ağu bulgusu (Serkan: boş iniş saatine değer yazılamıyordu): eski kod yalnız ISO deniyordu, plans.date ise OFP
 *  biçimindedir → Invalid Date → her deneme "cannot resolve a date". ARCHIVED FLTS saat düzeltmesi de buradan okur. */
export function planTarihi(deger) {
  if (!deger) return null;
  const iso = new Date(`${deger}T00:00:00Z`);
  if (!isNaN(iso.getTime())) return iso;
  const m = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/.exec(String(deger).trim());
  if (!m) return null;
  const ay = AYLAR.indexOf(m[2].toUpperCase());
  if (ay < 0) return null;
  return new Date(Date.UTC(Number(m[3]), ay, Number(m[1])));
}

const gunu = (deger) => { const d = planTarihi(deger); return d ? d.toISOString().slice(0, 10) : null; };

/** Görevin günü (YYYY-MM-DD) ve rotası için şirketin planı.
 *  Döner: { durum, plan, metin }
 *    'var'       → tek plan (ya da aynı gün birden çok plan ama tipi ve tescili aynı): `plan`
 *    'yok'       → o gün o rotada plan yok — öneri yok, planlamacı seçer
 *    'belirsiz'  → o gün o rotada tipi ya da tescili FARKLI birden çok plan — sistem seçmez
 *    'okunamadi' → okuma düştü ya da kesik geldi — "yok" DENMEZ
 *  `metin`: ekrana yazılacak cümle ('var'da null). */
export async function ayniGunPlani({ dep, dest, tarih, customerId = null }) {
  const DEP = String(dep || '').toUpperCase(), DEST = String(dest || '').toUpperCase();
  if (!/^[A-Z]{4}$/.test(DEP) || !/^[A-Z]{4}$/.test(DEST) || !/^\d{4}-\d{2}-\d{2}$/.test(String(tarih || ''))) {
    return { durum: 'yok', plan: null, metin: null };
  }
  const kapsam = q => (customerId ? q.eq('customer_id', customerId) : q);
  const r = await hepsiniOku(() => kapsam(supabase.from('plans').select('id,dep,dest,date,reg,operation_type,operation_type_source,fpl_remark,created_at', SAYILI)
    .eq('dep', DEP).eq('dest', DEST).not('operation_type', 'is', null).neq('status', 'deleted')));
  const hata = okumaHataMetni(r);
  if (hata) return { durum: 'okunamadi', plan: null, metin: `FLIGHT PLAN COULD NOT BE CHECKED (${hata}) — set manually` };
  const ayniGun = (r.data || []).filter(p => gunu(p.date) === tarih)
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  if (!ayniGun.length) return { durum: 'yok', plan: null, metin: 'NO MATCHING FLIGHT PLAN FOUND — set manually' };
  const farkli = new Set(ayniGun.map(p => `${p.operation_type}|${String(p.reg || '').toUpperCase()}`));
  if (farkli.size > 1) {
    return { durum: 'belirsiz', plan: null,
      metin: `${ayniGun.length} FLIGHT PLANS ON ${DEP}→${DEST} THAT DAY WITH DIFFERENT TYPE OR AIRCRAFT — set manually` };
  }
  return { durum: 'var', plan: ayniGun[0], metin: null };
}

/** EDIT DUTY'de tip elle düzeltilince kayda yazılan kaynak — plan durumu neyse o söylenir. */
export function elleDuzeltmeKaynagi(sonuc, opType) {
  if (sonuc?.durum === 'var') {
    const p = sonuc.plan;
    return p.operation_type !== opType
      ? `manual correction (flight plan: ${p.operation_type} — ${p.operation_type_source})`
      : `corrected to match flight plan — ${p.operation_type_source}`;
  }
  if (sonuc?.durum === 'okunamadi') return 'manual correction (flight plan could not be checked)';
  if (sonuc?.durum === 'belirsiz') return 'manual correction (more than one flight plan that day)';
  return 'manual correction (no matching flight plan)';
}
